import { Component, ChangeDetectionStrategy, OnInit, NgZone, inject, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { map, filter, startWith, distinctUntilChanged } from 'rxjs/operators';

import { mainMenu } from '@shared/configs/main-navigation';
import { MenuItem } from '@shared/models/menu-item.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { WindowService } from '@shared/window';

import { TranslateService } from '@ngx-translate/core';
import { MenuMobileComponent } from '../menu-mobile/menu-mobile.component';
import { MenuDesktopComponent } from '../menu-desktop/menu-desktop.component';
import { SearchComponent } from '../../search/components/search/search.component';
import { MenuLanguagesComponent } from '../menu-languages/menu-languages.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MenuMobileComponent, MenuDesktopComponent, SearchComponent, MenuLanguagesComponent, AsyncPipe],
})
export class HeaderComponent implements OnInit {
    private document = inject(DOCUMENT);
    private router = inject(Router);
    private ngZone = inject(NgZone);
    private windowService = inject(WindowService);
    private translateService = inject(TranslateService);

    menuItems: MenuItem[] = [];
    isMainPage$!: Observable<boolean>;
    isTopOfPage$ = new BehaviorSubject<boolean>(false);

    ngOnInit(): void {
        this.isMainPage$ = this.isMainPage;
        this.menuItems = mainMenu.filter((item) => item.languages?.includes(this.translateService.currentLang));

        this.watchForScroll();
    }

    /**
     * Check if is Main Page, based on URL change. If URL has 3 characters only `/{LANG}`, assume it's home page
     */
    get isMainPage(): Observable<boolean> {
        return this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.url),
            startWith(this.router.url),
            map((url: string) => {
                return url.length === 3;
            }),
            distinctUntilChanged(),
        );
    }

    get isBrowser(): boolean {
        return this.windowService.isBrowser;
    }

    /**
     * Is Compact
     */
    get compact$(): Observable<boolean> {
        return this.isTopOfPage$.pipe(distinctUntilChanged());
    }

    private watchForScroll(): void {
        this.ngZone.runOutsideAngular(() => {
            this.document.addEventListener('scroll', this.onWindowScroll, false);
        });
    }

    /**
     * Window Scroll Event
     */
    private onWindowScroll = (): void => {
        if (window.scrollY < 128) {
            this.ngZone.run(() => this.isTopOfPage$.next(false));
        } else {
            this.ngZone.run(() => this.isTopOfPage$.next(true));
        }
    };
}
